import React, { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import apiClient from '../services/api';
import { User, LoginRequest, LoginResponse, RegisterRequest, RegisterResponse } from '../types/api';
import showToast from '../utils/toastUtils';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  register: (userData: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (userData: Partial<User>) => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check for existing session on mount
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const storedUserData = localStorage.getItem('userData');
        
        if (token) {
          apiClient.setToken(token);
          
          // Try to validate token with API
          try {
            const response = await apiClient.getMe();
            if (response.success && response.data) {
              setUser(response.data);
              // Update stored user data with fresh data
              localStorage.setItem('userData', JSON.stringify(response.data));
            } else {
              // Token is invalid, clear it
              await clearAuthData();
            }
          } catch (error) {
            // API call failed, try to restore from localStorage as fallback
            if (storedUserData) {
              try {
                const parsedUser = JSON.parse(storedUserData);
                setUser(parsedUser);
              } catch (parseError) {
                console.error('Failed to parse stored user data:', parseError);
                await clearAuthData();
              }
            } else {
              await clearAuthData();
            }
          }
        } else if (storedUserData) {
          // No token but have stored user data, clear it (inconsistent state)
          localStorage.removeItem('userData');
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        await clearAuthData();
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const clearAuthData = async () => {
    try {
      // Clear all auth-related data
      apiClient.clearToken();
      
      // Clear localStorage auth data
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
      
      // Clear any other app-specific data if needed
      localStorage.removeItem('cart');
      localStorage.removeItem('preferences');
      
      // Clear cookies if any
      document.cookie.split(";").forEach((c) => {
        const eqPos = c.indexOf("=");
        const name = eqPos > -1 ? c.substr(0, eqPos) : c;
        if (name.includes('auth') || name.includes('session')) {
          document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
        }
      });
    } catch (error) {
      console.error('Error clearing auth data:', error);
    }
  };

  const login = async (credentials: LoginRequest) => {
    try {
      const response = await apiClient.login(credentials);
      
      if (response.success && response.data) {
        const { data: userData, token } = response;
        
        // Store token securely (setToken also stores in localStorage)
        if (token) {
          apiClient.setToken(token);
          localStorage.setItem('authToken', token);
        }
        
        // Store user data
        if (userData) {
          localStorage.setItem('userData', JSON.stringify(userData));
          setUser(userData);
        }
        
        // Show success message
        showToast.success(`Welcome back, ${userData?.name || 'User'}!`);
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      showToast.error(error.message || 'Login failed. Please try again.');
      throw error;
    }
  };

  const register = async (userData: RegisterRequest) => {
    try {
      const response = await apiClient.register(userData);
      
      if (response.success && response.data) {
        const { data: newUser, token } = response;
        
        // Store tokens securely
        apiClient.setToken(token);
        
        // Store user data
        localStorage.setItem('userData', JSON.stringify(newUser));
        
        setUser(newUser);
        
        // Show success message
        showToast.success(`Welcome to LeChalo, ${newUser.name}!`);
      } else {
        throw new Error(response.message || 'Registration failed');
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      showToast.error(error.message || 'Registration failed. Please try again.');
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Show loading state
      const loadingToast = toast.loading('Logging out...');
      
      // Call logout API to invalidate server-side session
      try {
        await apiClient.logout();
      } catch (error) {
        // Even if API call fails, we should still logout locally
        console.warn('Logout API call failed:', error);
      }
      
      // Clear all local data
      await clearAuthData();
      
      // Clear user state
      setUser(null);
      
      // Dismiss loading toast
      showToast.dismiss(loadingToast);
      
      // Show success message
      showToast.success('Logged out successfully');
      
      // Redirect to home page
      navigate('/', { replace: true });
      
    } catch (error) {
      console.error('Logout error:', error);
      showToast.error('Error during logout. Please try again.');
      
      // Even if there's an error, clear local data
      await clearAuthData();
      setUser(null);
      navigate('/', { replace: true });
    }
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      
      // Update stored user data
      localStorage.setItem('userData', JSON.stringify(updatedUser));
    }
  };

  const refreshUser = async () => {
    try {
      const response = await apiClient.getMe();
      if (response.success && response.data) {
        setUser(response.data);
        localStorage.setItem('userData', JSON.stringify(response.data));
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
    updateUser,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;